import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, CalendarPlus, ChevronDown, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { eventsAPI } from '../api/client';
import { isNativePlatform, platform } from '../native';
import { isShareCanceled } from '../native/openIcs';
import type { CalendarLinks } from '../types';

async function openCalendarLink(url: string, sameTab = false) {
  if (isNativePlatform) {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
    return;
  }
  if (sameTab) {
    window.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Only iPhone and iPad Safari hand an .ics link straight to Calendar; Mac,
// Windows, and Android browsers download it, so there the Apple option is the
// download itself.
function opensAppleCalendarDirectly(): boolean {
  if (isNativePlatform) return platform === 'ios';
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent || '')
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// Per-event calendar control. Opens the event straight in Google, Outlook, or
// Apple Calendar instead of downloading a file. With a connected calendar the
// event already syncs, so it shows "Added to calendar" plus the same menu for
// adding it somewhere else.
export default function EventCalendarAction({
  icsUrl,
  links,
  filename,
  connected,
  variant = 'solid',
}: {
  icsUrl: string | null;
  links?: CalendarLinks | null;
  filename: string;
  connected: boolean;
  variant?: 'solid' | 'outline' | 'ghost';
}) {
  const [open, setOpen] = useState(false);
  const [outlookOpen, setOutlookOpen] = useState(false);
  const [alignLeft, setAlignLeft] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggle = () => {
    const rect = menuRef.current?.getBoundingClientRect();
    // The menu is 14rem wide; open rightward when the button sits near the left edge.
    setAlignLeft(Boolean(rect && rect.right < 240));
    setOutlookOpen(false);
    setOpen((value) => !value);
  };

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | TouchEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  const download = async () => {
    if (!icsUrl) return;
    setDownloading(true);
    try {
      const mode = await eventsAPI.downloadIcs(icsUrl, filename);
      if (mode === 'web') toast.success('Calendar file downloaded');
    } catch (error) {
      if (isShareCanceled(error)) return;
      toast.error(isNativePlatform
        ? 'Could not add this to your calendar'
        : 'Could not download calendar file');
    } finally {
      setDownloading(false);
    }
  };

  const choose = async (url: string, sameTab = false) => {
    setOpen(false);
    try {
      await openCalendarLink(url, sameTab);
    } catch {
      toast.error('Could not open your calendar');
    }
  };

  if (!icsUrl && !links) return null;

  const appleDirect = opensAppleCalendarDirectly();
  const options = [
    appleDirect && links?.apple ? { label: 'Apple Calendar', run: () => choose(links.apple as string, true) } : null,
    links?.google ? { label: 'Google Calendar', run: () => choose(links.google) } : null,
  ].filter((option): option is { label: string; run: () => Promise<void> } => option !== null);
  const outlookChoices = [
    links?.outlook ? { label: 'Work or school', run: () => choose(links.outlook) } : null,
    links?.outlook_personal
      ? { label: 'Personal (outlook.com, hotmail)', run: () => choose(links.outlook_personal) }
      : null,
  ].filter((option): option is { label: string; run: () => Promise<void> } => option !== null);
  const hasMenu = options.length > 0 || outlookChoices.length > 0;
  const downloadLabel = appleDirect
    ? 'Download .ics file'
    : platform === 'android' || /Android/.test(navigator.userAgent || '')
      ? 'Calendar app (.ics file)'
      : 'Apple Calendar or other app (.ics file)';

  const menu = open && (
    <div
      role="menu"
      className={`absolute ${alignLeft ? 'left-0' : 'right-0'} top-full mt-1 z-30 w-56 rounded-xl border border-gray-200 bg-white py-1 shadow-lg`}
    >
      {options.map((option) => (
        <button
          key={option.label}
          type="button"
          role="menuitem"
          onClick={() => void option.run()}
          className="block w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-off-white"
        >
          {option.label}
        </button>
      ))}
      {outlookChoices.length > 0 && (
        <>
          <button
            type="button"
            role="menuitem"
            aria-expanded={outlookOpen}
            onClick={() => setOutlookOpen((value) => !value)}
            className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-off-white"
          >
            Outlook
            <ChevronDown size={14} className={`transition ${outlookOpen ? 'rotate-180' : ''}`} />
          </button>
          {outlookOpen && outlookChoices.map((option) => (
            <button
              key={option.label}
              type="button"
              role="menuitem"
              onClick={() => void option.run()}
              className="block w-full py-2 pl-8 pr-4 text-left text-sm text-gray-600 hover:bg-off-white"
            >
              {option.label}
            </button>
          ))}
        </>
      )}
      {icsUrl && (
        <button
          type="button"
          role="menuitem"
          onClick={() => { setOpen(false); void download(); }}
          disabled={downloading}
          className={`flex w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-left text-sm hover:bg-off-white disabled:opacity-60 ${
            appleDirect ? 'text-gray-500' : 'text-gray-700'
          }`}
        >
          <Download size={14} />
          {downloadLabel}
        </button>
      )}
    </div>
  );

  if (connected) {
    return (
      <div ref={menuRef} className="relative flex items-center gap-1 flex-shrink-0">
        <span className="inline-flex items-center gap-1.5 px-2 py-2 text-sm font-medium text-green-700">
          <CalendarCheck size={16} />
          Added to calendar
        </span>
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          className="inline-flex items-center gap-1 px-2.5 py-2 text-sm text-gray-500 hover:text-navy hover:bg-black/5 rounded-lg transition"
        >
          Add elsewhere
          <ChevronDown size={14} />
        </button>
        {menu}
      </div>
    );
  }

  return (
    <div ref={menuRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => (hasMenu ? toggle() : void download())}
        disabled={downloading}
        aria-haspopup={hasMenu ? 'menu' : undefined}
        aria-expanded={hasMenu ? open : undefined}
        className={`inline-flex items-center gap-1.5 text-sm rounded-lg transition disabled:opacity-60 ${
          variant === 'ghost'
            ? 'px-2.5 py-1.5 pointer-coarse:py-2.5 text-navy hover:bg-navy/5'
            : variant === 'outline'
              ? 'px-3 py-2 border border-navy text-navy hover:bg-off-white'
              : 'px-3 py-2 bg-navy text-white hover:bg-navy-dark'
        }`}
      >
        <CalendarPlus size={16} />
        Add to Calendar
        {hasMenu && <ChevronDown size={14} />}
      </button>
      {menu}
    </div>
  );
}

// One-per-card nudge for people with no connected calendar.
export function ConnectCalendarHint({
  connected,
  loading,
  compact = false,
}: {
  connected: boolean;
  loading: boolean;
  compact?: boolean;
}) {
  if (loading || connected) return null;
  if (compact) {
    return (
      <Link
        to="/settings?tab=account"
        className="text-sm text-gray-500 hover:text-navy transition"
      >
        Connect calendar
      </Link>
    );
  }
  return (
    <p className="text-xs text-gray-400">
      <Link to="/settings?tab=account" className="text-gold hover:underline font-medium">
        Connect your calendar
      </Link>{' '}
      to sync these automatically.
    </p>
  );
}
